/**
 * TopMeeps · bottom-nav icons
 *
 * Single-color SVG icons that inherit `currentColor` from the parent
 * (the nav sets `--ink` for the active tab and `--ink-3` otherwise).
 *
 * Each icon renders at ~22 px tall by default. ViewBox widths vary
 * per icon — the nav cell centres them.
 *
 * Overlapping meeples (Friends, Groups) carry a small `paper-2`
 * stroke so they read as distinct figures against each other and
 * against the nav background. That single hardcoded color is the
 * intentional exception called out in the design brief.
 *
 * Delivered by Claude Design; see handoff/design_handoff_nav_icons/.
 */

import type { SVGProps } from 'react'

// Background-of-nav color used as a hairline between overlapping
// silhouettes. If the nav bg is ever themed differently, this is the
// one knob to turn.
const NAV_BG = 'var(--paper-2)'

// Reusable meeple silhouette path (40-unit normalized box).
// Identical geometry to `MeepleAvatar`, so the people in the nav are
// the same people as the avatars throughout the product.
const MEEPLE =
  'M20 4 C24 4 26 7 26 10 C26 12 25 13 25 14 L31 14 C34 14 36 16 36 19 ' +
  'L36 22 C36 24 35 25 33 25 L29 25 L29 33 C29 35 27 37 25 37 ' +
  'L15 37 C13 37 11 35 11 33 L11 25 L7 25 C5 25 4 24 4 22 L4 19 ' +
  'C4 16 6 14 9 14 L15 14 C15 13 14 12 14 10 C14 7 16 4 20 4 Z'

type IconProps = SVGProps<SVGSVGElement> & {
  /** Pixel height. Defaults to 22 — the nav's target render size. */
  size?: number
}

const baseProps = (size: number, viewBox: string) =>
  ({
    height: size,
    viewBox,
    fill: 'currentColor',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
    focusable: false,
    style: { display: 'block' },
  }) as const

// ─── Home ─────────────────────────────────────────────────────────────
// Peaked-roof house with a door cutout. Slight roof overhang so it
// reads as "cozy hut" rather than "Monopoly piece". Solid silhouette,
// no stroke, no chimney (too noisy at 22 px).

export const HomeIcon = ({ size = 22, ...rest }: IconProps) => (
  <svg {...baseProps(size, '0 0 24 24')} {...rest}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="
        M12 2.6
        L22 11.1
        L20 11.1
        L20 21.4
        C20 21.7 19.8 21.9 19.5 21.9
        L4.5 21.9
        C4.2 21.9 4 21.7 4 21.4
        L4 11.1
        L2 11.1
        Z
        M10.2 21.9
        L10.2 14.6
        C10.2 14.3 10.4 14.1 10.7 14.1
        L13.3 14.1
        C13.6 14.1 13.8 14.3 13.8 14.6
        L13.8 21.9
        Z
      "
    />
  </svg>
)

// ─── Me ───────────────────────────────────────────────────────────────
// One meeple, dead-centered. Same silhouette as `MeepleAvatar` — the
// user's nav icon is literally the same shape as their avatar.

export const MeIcon = ({ size = 22, ...rest }: IconProps) => (
  <svg {...baseProps(size, '0 0 22 24')} {...rest}>
    <g transform="translate(0, 1.5) scale(0.55)">
      <path d={MEEPLE} />
    </g>
  </svg>
)

// ─── Friends ──────────────────────────────────────────────────────────
// Two meeples standing close enough that their inner arms naturally
// merge into a single horizontal bar — "arms linked / clasped hands".
// A thin `paper-2` halo around the front meeple keeps the two heads
// distinct without carving a gap through the merged arms.
//
// ViewBox is 0 0 30 24 — 1u of padding on each side so the right
// meeple's right arm renders fully, matching its left.

export const FriendsIcon = ({ size = 22, ...rest }: IconProps) => (
  <svg {...baseProps(size, '0 0 30 24')} {...rest}>
    {/* back meeple — left, plain fill */}
    <g transform="translate(-1, 2) scale(0.5)">
      <path d={MEEPLE} />
    </g>
    {/* front meeple — right, thin nav-bg halo to separate heads only */}
    <g transform="translate(10, 2) scale(0.5)">
      <path
        d={MEEPLE}
        stroke={NAV_BG}
        strokeWidth={1.4}
        strokeLinejoin="round"
        paintOrder="stroke"
      />
    </g>
  </svg>
)

// ─── Groups ───────────────────────────────────────────────────────────
// Three meeples in a triangular cluster framed inside a double-ring
// stamp. The frame reads as a club / membership seal and lets us keep
// the count low (three) while still saying "a group, not just two
// people". Inner meeples carry a thin nav-bg halo so they read as
// three distinct figures.

export const GroupsIcon = ({ size = 22, ...rest }: IconProps) => (
  <svg {...baseProps(size, '0 0 24 24')} {...rest}>
    {/* stamp frame — outer ring + inner accent ring */}
    <circle cx={12} cy={12} r={11} fill="none" stroke="currentColor" strokeWidth={1.4} />
    <circle cx={12} cy={12} r={9.4} fill="none" stroke="currentColor" strokeWidth={0.7} />
    {/* back-top meeple */}
    <g transform="translate(7, 3) scale(0.26)">
      <path
        d={MEEPLE}
        stroke={NAV_BG}
        strokeWidth={1.6 / 0.26}
        strokeLinejoin="round"
        paintOrder="stroke"
      />
    </g>
    {/* front-left meeple */}
    <g transform="translate(2, 8.8) scale(0.26)">
      <path
        d={MEEPLE}
        stroke={NAV_BG}
        strokeWidth={1.6 / 0.26}
        strokeLinejoin="round"
        paintOrder="stroke"
      />
    </g>
    {/* front-right meeple */}
    <g transform="translate(12, 8.8) scale(0.26)">
      <path
        d={MEEPLE}
        stroke={NAV_BG}
        strokeWidth={1.6 / 0.26}
        strokeLinejoin="round"
        paintOrder="stroke"
      />
    </g>
  </svg>
)

// ─── Record · default hero ────────────────────────────────────────────
// The plain plus. Lives on the raised terracotta disc so the nav
// itself supplies most of the visual weight; the glyph just needs to
// read as "add". 3 px stroke, round caps.

export const RecordPlusIcon = ({ size = 22, ...rest }: IconProps) => (
  <svg {...baseProps(size, '0 0 24 24')} {...rest} fill="none">
    <path
      d="M12 5 L12 19 M5 12 L19 12"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
    />
  </svg>
)

// ─── Record · die alternative (proposal) ──────────────────────────────
// A d6 face showing a single pip in the centre. Reads as "roll" /
// "record a play" without the plus glyph.

export const RecordDieIcon = ({ size = 22, ...rest }: IconProps) => (
  <svg {...baseProps(size, '0 0 24 24')} {...rest} fill="none">
    <rect x={3.5} y={3.5} width={17} height={17} rx={3} stroke="currentColor" strokeWidth={2} />
    <circle cx={12} cy={12} r={2} fill="currentColor" />
  </svg>
)
